import React, { useMemo, useState } from 'react';
import Map, { Marker, Popup, NavigationControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

import { STATUS_COLOR, STATUS_LABEL, isBad } from '../lib/status.js';
import { useClusters } from '../hooks/useClusters.js';

// Style raster simples via CARTO CDN (uso permitido com atribuição).
// Para qualidade vetorial, substituir por Protomaps no futuro (PMTiles + style.json).
const MAP_STYLE = {
  version: 8,
  glyphs: 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf',
  sources: {
    carto: {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png',
        'https://b.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png',
        'https://c.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png',
      ],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors © CARTO',
    },
  },
  layers: [{ id: 'carto', type: 'raster', source: 'carto' }],
};

// Centro default — Belo Horizonte (UNE atende MG; ajustar conforme cluster real).
const DEFAULT_VIEW = { latitude: -19.92, longitude: -43.94, zoom: 11 };

// Limiares de transição cluster ↔ marker individual (MAP-20 + fix 2026-05-15
// + v3 2026-05-15).
//
// V3: como o snapshot traz TODAS as ONUs (sem paginação no backend), o mapa
// pode renderizar markers individuais até um limite confortável de DOM/render
// — 1500 markers é o ponto onde o browser começa a engasgar com Map markers
// react-map-gl. Acima disso, fallback para clusters server-side.
//
//   - rows.length <= MARKERS_BUDGET    →  markers (cabe no DOM, melhor UX)
//   - rows.length >  MARKERS_BUDGET    →  clusters (visão global agregada)
//   - zoom >= 14 SEMPRE markers        →  close-up sempre mostra pins
//                                          (cabe naturalmente porque o
//                                          viewport recorta o bbox)
const ZOOM_FORCE_MARKERS = 14;
const MARKERS_BUDGET = 1500;

function MarkerDot({ status, badPulse }) {
  const color = STATUS_COLOR[status] || '#6b7280';
  const size = isBad(status) ? 14 : 8;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        border: '2px solid rgba(255,255,255,0.9)',
        boxShadow: badPulse ? `0 0 0 3px ${color}55, 0 0 12px ${color}80` : `0 1px 3px rgba(0,0,0,0.3)`,
        animation: badPulse ? 'mapPulse 1.6s ease-in-out infinite' : 'none',
      }}
    />
  );
}

function clusterColor(count, countBad) {
  const ratio = count > 0 ? countBad / count : 0;
  if (ratio === 0) return '#10b981'; // verde — todos online
  if (ratio < 0.2) return '#fbbf24'; // amarelo
  if (ratio < 0.5) return '#f97316'; // laranja
  return '#dc2626'; // vermelho — maioria em problema
}

function ClusterBubble({ count, countBad, onClick }) {
  const size = Math.round(28 + Math.log2(count + 1) * 7);
  const color = clusterColor(count, countBad);
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: count >= 1000 ? 11 : 13,
        cursor: 'pointer',
        border: '2px solid rgba(255,255,255,0.95)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
        userSelect: 'none',
      }}
      title={`${count} ONUs (${countBad} em problema)`}
    >
      {count}
    </div>
  );
}

const pulseCSS = `
@keyframes mapPulse {
  0%   { transform: scale(1);   opacity: 1;   }
  50%  { transform: scale(1.4); opacity: 0.6; }
  100% { transform: scale(1);   opacity: 1;   }
}
`;

function bboxFromMap(map) {
  if (!map) return null;
  const b = map.getBounds();
  // formato esperado pelo backend: lat_min,lng_min,lat_max,lng_max
  return `${b.getSouth()},${b.getWest()},${b.getNorth()},${b.getEast()}`;
}

export function MapCanvas({
  onus = [],
  onSelectOnu,
  selectedSn,
  focusOn,
  statusFilter = 'bad',
  // Filtros propagados ao endpoint /clusters para o server-side bater 1:1
  // com a tabela.
  search,
  selectedOlts,
  minAgingMin,
  maxAgingMin,
  // matchedCount = total do filtro NO BACKEND (vs onus.length = página atual).
  // Se matched > onus.length, sabemos que a tabela paginou e o mapa precisa
  // usar clusters server-side para mostrar TODAS as ONUs.
  matchedCount = 0,
}) {
  const [hover, setHover] = useState(null);
  const [viewState, setViewState] = useState(DEFAULT_VIEW);
  const [bbox, setBbox] = useState(null);

  // Se algo está focado, recentra ao mudar.
  React.useEffect(() => {
    if (focusOn && focusOn.lat && focusOn.lng) {
      setViewState((vs) => ({ ...vs, latitude: focusOn.lat, longitude: focusOn.lng, zoom: Math.max(14, vs.zoom) }));
    }
  }, [focusOn]);

  const zoom = viewState.zoom;

  // V3: snapshot já trouxe TUDO. Não há "paginated" — onus é o conjunto
  // completo filtrado client-side. Só fazemos cluster se passar do budget
  // de DOM (rara visão "Online" com 9k pontos).
  const wantClusters = useMemo(() => {
    if (zoom >= ZOOM_FORCE_MARKERS) return false; // close-up = sempre markers
    return onus.length > MARKERS_BUDGET;
  }, [zoom, onus.length]);

  // Propaga TODOS os filtros do user pro server-side cluster. Sem isso o
  // mapa cluster pode mostrar ONUs que não batem com a tabela filtrada.
  const oltCsv =
    selectedOlts && selectedOlts.length ? selectedOlts.join(',') : undefined;
  const { clusters } = useClusters({
    bbox,
    zoom: Math.round(zoom),
    status: statusFilter,
    olt: oltCsv,
    search,
    minAgingMin,
    maxAgingMin,
    enabled: wantClusters,
  });

  const markers = useMemo(
    () =>
      onus
        .filter((o) => o.install_lat != null && o.install_lng != null)
        .map((o) => ({
          sn: o.onu_sn,
          lat: Number(o.install_lat),
          lng: Number(o.install_lng),
          status: o.status,
          person: o.person_name,
          contract: o.contract_code || o.contract_id,
          olt: o.olt_code,
          since: o.status_since,
          signal_rx: o.signal_rx_dbm,
        })),
    [onus]
  );

  const handleMove = (e) => {
    setViewState(e.viewState);
    const next = bboxFromMap(e.target);
    if (next) setBbox(next);
  };

  const handleLoad = (e) => {
    const next = bboxFromMap(e.target);
    if (next) setBbox(next);
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <style>{pulseCSS}</style>
      <Map
        {...viewState}
        onMove={handleMove}
        onLoad={handleLoad}
        style={{ width: '100%', height: '100%' }}
        mapStyle={MAP_STYLE}
        attributionControl={true}
      >
        <NavigationControl position="top-right" />

        {wantClusters
          ? clusters.map((c) => (
              <Marker
                key={`c-${c.lat.toFixed(5)}-${c.lng.toFixed(5)}`}
                longitude={c.lng}
                latitude={c.lat}
                anchor="center"
              >
                <ClusterBubble
                  count={c.count}
                  countBad={c.count_bad}
                  onClick={() =>
                    setViewState((vs) => ({
                      ...vs,
                      latitude: c.lat,
                      longitude: c.lng,
                      zoom: Math.min(18, vs.zoom + 2),
                    }))
                  }
                />
              </Marker>
            ))
          : markers.map((m) => (
              <Marker
                key={m.sn}
                longitude={m.lng}
                latitude={m.lat}
                anchor="center"
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  onSelectOnu?.(m.sn);
                }}
              >
                <div
                  onMouseEnter={() => setHover(m)}
                  onMouseLeave={() => setHover(null)}
                  style={{ cursor: 'pointer' }}
                >
                  <MarkerDot status={m.status} badPulse={isBad(m.status) || selectedSn === m.sn} />
                </div>
              </Marker>
            ))}

        {!wantClusters && hover && (
          <Popup
            longitude={hover.lng}
            latitude={hover.lat}
            anchor="bottom"
            closeButton={false}
            offset={12}
          >
            <div style={{ fontSize: 12, lineHeight: 1.4 }}>
              <div style={{ fontWeight: 700 }}>{hover.person || 'sem cadastro'}</div>
              <div style={{ color: '#6b7280' }}>contrato {hover.contract || '—'}</div>
              <div style={{ color: STATUS_COLOR[hover.status], fontWeight: 600 }}>
                {STATUS_LABEL[hover.status] || 'unknown'}
              </div>
              <div style={{ color: '#6b7280' }}>OLT {hover.olt || '—'}</div>
            </div>
          </Popup>
        )}
      </Map>
    </div>
  );
}
